import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  ChevronDown,
  ChevronRight,
  CheckCircle,
  XCircle,
  Clock,
  ExternalLink,
  ThumbsUp,
  ThumbsDown,
} from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import GoalsConstants from '../../../../../constants/goals.constants';
import { cn } from '../../../../../lib/utils';

function SkillReviewItem({ skill, isExpanded, onToggle, onApprove, onDecline }) {
  const [declineComment, setDeclineComment] = useState('');
  const [recommendation, setRecommendation] = useState('');
  const [activeDeclineId, setActiveDeclineId] = useState(null);
  const [activeApproveId, setActiveApproveId] = useState(null);

  const getStatusIcon = (status) => {
    switch (status) {
      case GoalsConstants.GOAL_STATUSES.APPROVED:
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case GoalsConstants.GOAL_STATUSES.DECLINED:
        return <XCircle className="h-4 w-4 text-red-500" />;
      case GoalsConstants.GOAL_STATUSES.DONE:
        return <Clock className="h-4 w-4 text-blue-500" />;
      default:
        return <Clock className="h-4 w-4 text-amber-500" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case GoalsConstants.GOAL_STATUSES.APPROVED:
        return (
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/10 text-green-600">
            Approved
          </span>
        );
      case GoalsConstants.GOAL_STATUSES.DECLINED:
        return (
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/10 text-red-600">
            Declined
          </span>
        );
      case GoalsConstants.GOAL_STATUSES.DONE:
        return (
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/10 text-blue-600">
            Ready for Review
          </span>
        );
      case GoalsConstants.GOAL_STATUSES.IN_PROGRESS:
        return (
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/10 text-amber-600">
            In Progress
          </span>
        );
      default:
        return (
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-500/10 text-gray-600">
            {status || 'Pending'}
          </span>
        );
    }
  };

  const handleApprove = (detail) => {
    onApprove(skill._id, detail.itemId, recommendation);
    setActiveApproveId(null);
    setRecommendation('');
  };

  const handleDecline = (detail) => {
    onDecline({ goalId: skill._id, itemId: detail.itemId, comment: declineComment });
    setActiveDeclineId(null);
    setDeclineComment('');
  };

  const allApproved = skill.preparedDetails.every(
    (d) => d.status === GoalsConstants.GOAL_STATUSES.APPROVED,
  );
  const allDeclined = skill.preparedDetails.every(
    (d) => d.status === GoalsConstants.GOAL_STATUSES.DECLINED,
  );
  const hasAllReviewed = skill.preparedDetails.every(
    (d) =>
      d.status === GoalsConstants.GOAL_STATUSES.APPROVED ||
      d.status === GoalsConstants.GOAL_STATUSES.DECLINED,
  );

  return (
    <div
      className={cn(
        'border rounded-lg overflow-hidden transition-all',
        hasAllReviewed && 'opacity-75',
      )}
      style={{ borderColor: 'hsl(var(--border))' }}
    >
      {/* Header */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronDown className="h-5 w-5 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          )}
          <span className="font-medium">{skill.name}</span>
          <span className="text-sm text-muted-foreground">
            ({skill.preparedDetails.length}{' '}
            {skill.preparedDetails.length === 1 ? 'skill' : 'skills'})
          </span>
        </div>
        <div className="flex items-center gap-2">
          {allApproved && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/10 text-green-600">
              All Approved
            </span>
          )}
          {allDeclined && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/10 text-red-600">
              All Declined
            </span>
          )}
          {!allApproved && !allDeclined && hasAllReviewed && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/10 text-blue-600">
              Reviewed
            </span>
          )}
          {!hasAllReviewed && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/10 text-amber-600">
              Processing
            </span>
          )}
        </div>
      </button>

      {/* Details */}
      {isExpanded && (
        <div className="border-t" style={{ borderColor: 'hsl(var(--border))' }}>
          {skill.preparedDetails.map((detail, index) => (
            <div
              key={detail.itemId}
              className={cn(
                'p-4 border-b last:border-b-0',
                index % 2 === 0 ? 'bg-muted' : 'bg-background',
              )}
              style={{ borderColor: 'hsl(var(--border))' }}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(detail.status)}
                    <span className="font-medium text-sm">{detail.name}</span>
                    {getStatusBadge(detail.status)}
                  </div>

                  {/* Success Criteria */}
                  <div className="ml-6 space-y-1">
                    <p className="text-xs text-muted-foreground font-medium">Success Criteria:</p>
                    <ul className="text-sm text-muted-foreground list-disc list-inside">
                      {detail.successCriteria.map((criteria, idx) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <li key={idx}>{criteria}</li>
                      ))}
                    </ul>
                  </div>

                  {/* Materials */}
                  <div className="ml-6 space-y-1">
                    <p className="text-xs text-muted-foreground font-medium">Materials:</p>
                    <div className="flex flex-wrap gap-2">
                      {detail.materials.map((material, idx) => (
                        <a
                          // eslint-disable-next-line react/no-array-index-key
                          key={idx}
                          href={material.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-muted hover:bg-muted/80 transition-colors"
                        >
                          <span className="text-muted-foreground">{material.type}:</span>
                          <span>{material.name}</span>
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ))}
                    </div>
                  </div>

                  {/* Comment/Recommendation display */}
                  {detail.comment && (
                    <div className="ml-6 p-2 rounded bg-red-500/10 text-sm">
                      <span className="font-medium text-red-600">Decline reason:</span>{' '}
                      {detail.comment}
                    </div>
                  )}
                  {detail.recommendation && (
                    <div className="ml-6 p-2 rounded bg-green-500/10 text-sm">
                      <span className="font-medium text-green-600">Recommendation:</span>{' '}
                      {detail.recommendation}
                    </div>
                  )}
                </div>

                {/* Actions - show for statuses that need review */}
                {detail.status !== GoalsConstants.GOAL_STATUSES.APPROVED &&
                  detail.status !== GoalsConstants.GOAL_STATUSES.DECLINED && (
                    <div className="flex flex-col gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                        onClick={() => setActiveApproveId(detail.itemId)}
                      >
                        <ThumbsUp className="h-4 w-4 mr-1" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setActiveDeclineId(detail.itemId)}
                      >
                        <ThumbsDown className="h-4 w-4 mr-1" />
                        Decline
                      </Button>
                    </div>
                  )}
              </div>

              {/* Approve Input */}
              {activeApproveId === detail.itemId && (
                <div className="mt-3 ml-6 p-3 rounded-lg bg-green-500/5 border border-green-500/20 space-y-2">
                  <input
                    type="text"
                    value={recommendation}
                    onChange={(e) => setRecommendation(e.target.value)}
                    placeholder="Add recommendation (optional)"
                    className={cn(
                      'w-full px-3 py-2 rounded-md text-sm',
                      'bg-background border border-input',
                      'focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent',
                    )}
                  />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => handleApprove(detail)}>
                      Confirm Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setActiveApproveId(null);
                        setRecommendation('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {/* Decline Input */}
              {activeDeclineId === detail.itemId && (
                <div className="mt-3 ml-6 p-3 rounded-lg bg-red-500/5 border border-red-500/20 space-y-2">
                  <textarea
                    value={declineComment}
                    onChange={(e) => setDeclineComment(e.target.value)}
                    placeholder="Reason for declining (required)"
                    rows={2}
                    className={cn(
                      'w-full px-3 py-2 rounded-md text-sm resize-none',
                      'bg-background border border-input',
                      'focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent',
                    )}
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDecline(detail)}
                      disabled={!declineComment.trim()}
                    >
                      Confirm Decline
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setActiveDeclineId(null);
                        setDeclineComment('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

SkillReviewItem.propTypes = {
  skill: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    itemId: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    preparedDetails: PropTypes.arrayOf(
      PropTypes.shape({
        itemId: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        status: PropTypes.string,
        successCriteria: PropTypes.arrayOf(PropTypes.string).isRequired,
        materials: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string,
            type: PropTypes.string,
            link: PropTypes.string,
          }),
        ).isRequired,
        comment: PropTypes.string,
        recommendation: PropTypes.string,
      }),
    ).isRequired,
  }).isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onApprove: PropTypes.func.isRequired,
  onDecline: PropTypes.func.isRequired,
};

export default SkillReviewItem;
